export interface RateLimitResult {
  success: boolean
  limit: number
  remaining: number
  reset: number // unix timestamp in ms
}

// In-memory sliding window: key -> sorted list of request timestamps (ms)
const store = new Map<string, number[]>()

let lastPruned = Date.now()
function pruneIfNeeded() {
  if (Date.now() - lastPruned < 60_000) return
  lastPruned = Date.now()
  for (const [key, timestamps] of store) {
    if (timestamps.length === 0) store.delete(key)
  }
}

export function checkRateLimitInMemory(
  key: string,
  limit: number,
  windowSeconds: number,
): RateLimitResult {
  pruneIfNeeded()
  const now = Date.now()
  const windowMs = windowSeconds * 1000
  const windowStart = now - windowMs

  const timestamps = (store.get(key) ?? []).filter((t) => t > windowStart)

  if (timestamps.length >= limit) {
    return {
      success: false,
      limit,
      remaining: 0,
      reset: timestamps[0] + windowMs,
    }
  }

  timestamps.push(now)
  store.set(key, timestamps)

  return {
    success: true,
    limit,
    remaining: limit - timestamps.length,
    reset: now + windowMs,
  }
}

export function _resetStoreForTests() {
  store.clear()
}

export async function checkRateLimit(
  key: string,
  limit: number,
  windowSeconds: number,
): Promise<RateLimitResult> {
  if (process.env.UPSTASH_REDIS_REST_URL && process.env.UPSTASH_REDIS_REST_TOKEN) {
    try {
      const { Ratelimit } = await import('@upstash/ratelimit')
      const { Redis } = await import('@upstash/redis')

      const redis = new Redis({
        url: process.env.UPSTASH_REDIS_REST_URL,
        token: process.env.UPSTASH_REDIS_REST_TOKEN,
      })

      const ratelimit = new Ratelimit({
        redis,
        limiter: Ratelimit.slidingWindow(limit, `${windowSeconds} s`),
        prefix: 'ask-qien-rl',
      })

      const result = await ratelimit.limit(key)
      return {
        success: result.success,
        limit: result.limit,
        remaining: result.remaining,
        reset: result.reset,
      }
    } catch {
      // Upstash unreachable or package not installed — degrade to in-memory
    }
  }

  return checkRateLimitInMemory(key, limit, windowSeconds)
}

export function extractIp(request: { headers: { get(name: string): string | null } }): string {
  const forwarded = request.headers.get('x-forwarded-for')
  if (forwarded) return forwarded.split(',')[0].trim()
  return request.headers.get('x-real-ip') ?? '127.0.0.1'
}
